import * as React from "react";
import { WorkflowNode } from "../../types";
import { Divider, FormGroup, ToggleGroup } from "../RightAsideHelpers";
import Input from "@/components/Input";
import TextArea from "@/components/TextArea";
import Select from "@/components/Select";
import { useMemo } from "react";

interface Props {
  node: WorkflowNode;
  onUpdate: (node: Partial<WorkflowNode>) => void;
}

export default function SendSmsEditor({ node, onUpdate }: Props) {
  const phoneSourceOptions = useMemo(() => ({
    callerID: "Caller's Phone Number (Safest)",
    attribute: "Collected Phone Number",
    fallback: "Use Email as Fallback"
  }), []);

  const fallbackActionOptions = useMemo(() => ({
    skipSms: "Skip SMS & Continue",
    useEmail: "Send Email Instead",
    pauseWorkflow: "Pause Workflow"
  }), []);

  return (
    <>
      <FormGroup
        label="Phone Number Source"
        tooltipText="Choose where to get the phone number: Caller's number (most secure), a collected attribute, or fallback to email"
      >
        <Select
          options={phoneSourceOptions}
          value={node.phoneSource || 'callerID'}
          onChange={(phoneSource) => onUpdate({ phoneSource: phoneSource as 'callerID' | 'attribute' | 'fallback' })}
        />
      </FormGroup>

      {node.phoneSource === 'attribute' && (
        <FormGroup
          label="Contact/Conversation Attribute"
          tooltipText="Name of the attribute containing the phone number (e.g., 'collectedPhone', 'customerPhone')"
        >
          <Input
            type="text"
            placeholder="e.g., collectedPhone"
            value={node.phoneAttribute || ""}
            onChange={(phoneAttribute) => onUpdate({ phoneAttribute })}
          />
        </FormGroup>
      )}

      <Divider />

      <FormGroup label="SMS Message">
        <TextArea
          value={node.smsMessage || ""}
          onChange={(e) => onUpdate({ smsMessage: e.currentTarget.value })}
          placeholder="Enter your SMS message. Use {{variable}} syntax for dynamic content (e.g., {{firstName}}, {{orderNumber}}, {{otp}})"
        />
      </FormGroup>

      <div className="text-sm text-slate-600 mb-4">
        <div className="font-medium mb-2">Available Variables:</div>
        <div className="space-y-1 text-xs">
          <div><strong>System:</strong> {'{{callerPhoneNumber}}, {{siteId}}, {{conversationId}}'}</div>
          <div><strong>Contact:</strong> {'{{firstName}}, {{lastName}}, {{email}}, {{phone}}'}</div>
          <div><strong>Conversation:</strong> {'{{collectedPhone}}, {{collectedEmail}}, {{orderNumber}}, {{ticketId}}'}</div>
          <div><strong>Workflow:</strong> {'{{otp}}, {{authCode}}, {{verificationLink}}'}</div>
        </div>
      </div>

      <ToggleGroup
        toggled={node.confirmRecipient || false}
        label="Ask for Recipient Confirmation"
        helpText="Ask user to confirm the phone number before sending. Best practice for user-provided numbers."
        changeToggle={() => {
          onUpdate({ confirmRecipient: !node.confirmRecipient });
        }}
      />

      <Divider />

      <FormGroup
        label="Max SMS Per Day"
        tooltipText="Leave blank for unlimited. Prevents spam by limiting messages sent to same customer per day."
      >
        <Input
          type="number"
          value={node.dailyLimit || ""}
          onChange={(dailyLimit) => onUpdate({ dailyLimit: dailyLimit ? Number(dailyLimit) : undefined })}
          placeholder="e.g., 5"
        />
      </FormGroup>

      <FormGroup
        label="If Phone Number Not Available"
        tooltipText="What should happen if we can't determine the phone number"
      >
        <Select
          options={fallbackActionOptions}
          value={node.fallbackAction || 'skipSms'}
          onChange={(fallbackAction) => onUpdate({ fallbackAction: fallbackAction as 'skipSms' | 'useEmail' | 'pauseWorkflow' })}
        />
      </FormGroup>

      {node.fallbackAction === 'useEmail' && (
        <FormGroup
          label="Email Attribute for Fallback"
          tooltipText="The attribute containing the email address to use if SMS fails"
        >
          <Input
            type="text"
            placeholder="e.g., email, collectedEmail"
            value={node.fallbackEmailAttribute || ""}
            onChange={(fallbackEmailAttribute) => onUpdate({ fallbackEmailAttribute })}
          />
        </FormGroup>
      )}

      <Divider />

      <ToggleGroup
        toggled={node.enableTranscript || false}
        label="Log SMS in Conversation Transcript"
        helpText="When enabled, the SMS message sent will be logged in the conversation for compliance and record-keeping."
        changeToggle={() => {
          onUpdate({ enableTranscript: !node.enableTranscript });
        }}
      />
    </>
  );
}
